import React, { useState, useEffect, useRef, useCallback } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface TrackData {
  token_number: number;
  token_code: string;
  customer_name: string;
  customer_phone?: string;
  service?: string;
  counter_name?: string;
  status: string;
  appointment_time?: string;
  is_walk_in: boolean;
  created_at: string;
  called_at?: string;
  queue_name: string;
  customer_label: string;
  counter_label: string;
  current_number: number;
  tokens_ahead: number;
  estimated_wait_mins: number;
  is_your_turn: boolean;
  is_open: boolean;
  date: string;
}

// ── Alarm ─────────────────────────────────────────────────────────────────────

let alarmCtx: AudioContext | null = null;
let bellInterval: any = null;

// Plays one pleasant two-note "ding-dong" bell
function playBell() {
  if (!alarmCtx) return;
  const now = alarmCtx.currentTime;
  const notes = [
    { freq: 988, start: 0 },     // ding (B5)
    { freq: 784, start: 0.28 },  // dong (G5)
  ];
  notes.forEach(({ freq, start }) => {
    const o = alarmCtx!.createOscillator();
    const g = alarmCtx!.createGain();
    o.connect(g);
    g.connect(alarmCtx!.destination);
    o.type = 'sine';
    const t = now + start;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    o.frequency.setValueAtTime(freq, t);
    o.start(t);
    o.stop(t + 0.9);
  });
}

function startAlarm() {
  if (bellInterval) return; // already running
  try {
    if (!alarmCtx) {
      alarmCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (alarmCtx.state === 'suspended') alarmCtx.resume();
    playBell(); // ring immediately
    bellInterval = setInterval(playBell, 2000); // repeat every 2s
  } catch {}
}

function stopAlarm() {
  if (bellInterval) {
    clearInterval(bellInterval);
    bellInterval = null;
  }
}

function unlockAudio(): boolean {
  try {
    if (!alarmCtx) {
      alarmCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (alarmCtx.state === 'suspended') {
      alarmCtx.resume();
    }
    // Play a near-silent blip synchronously inside the tap to fully unlock iOS/Android
    const o = alarmCtx.createOscillator();
    const g = alarmCtx.createGain();
    g.gain.value = 0.001;
    o.connect(g);
    g.connect(alarmCtx.destination);
    o.start();
    o.stop(alarmCtx.currentTime + 0.05);
    return true;
  } catch {
    return false;
  }
}

function vibrate() {
  try { navigator.vibrate?.([200, 100, 200, 100, 400]); } catch {}
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, {
  emoji: string; title: string; subtitle: string;
  bg: string; card: string; accent: string; text: string;
}> = {
  waiting: {
    emoji: '⏳', title: 'In Queue', subtitle: 'Please wait — we\'ll notify you',
    bg: '#f8fafc', card: 'rgba(99,102,241,0.08)', accent: '#6366f1', text: '#4338ca',
  },
  called: {
    emoji: '🔔', title: 'YOUR TURN!', subtitle: 'Please proceed now',
    bg: '#f0fdf4', card: 'rgba(34,197,94,0.1)', accent: '#16a34a', text: '#15803d',
  },
  urgent: {
    emoji: '🚨', title: 'Priority!', subtitle: 'You\'ve been marked urgent',
    bg: '#fff7ed', card: 'rgba(249,115,22,0.1)', accent: '#ea580c', text: '#c2410c',
  },
  hold: {
    emoji: '⏸', title: 'On Hold', subtitle: 'You\'ve been placed on hold',
    bg: '#fefce8', card: 'rgba(234,179,8,0.1)', accent: '#ca8a04', text: '#92400e',
  },
  done: {
    emoji: '✅', title: 'Done', subtitle: 'Your visit is complete',
    bg: '#f8fafc', card: 'rgba(148,163,184,0.1)', accent: '#64748b', text: '#475569',
  },
  skipped: {
    emoji: '⏭', title: 'Skipped', subtitle: 'Please contact reception',
    bg: '#fff1f2', card: 'rgba(239,68,68,0.08)', accent: '#dc2626', text: '#b91c1c',
  },
  cancelled: {
    emoji: '❌', title: 'Cancelled', subtitle: 'This token has been cancelled',
    bg: '#fff1f2', card: 'rgba(239,68,68,0.08)', accent: '#dc2626', text: '#b91c1c',
  },
};

// ── Animated ring for "your turn" ─────────────────────────────────────────────

function PulseRing({ color }: { color: string }) {
  return (
    <div style={{ position: 'absolute', inset: -16, borderRadius: '50%', border: `3px solid ${color}`, animation: 'pulse-ring 1.5s ease-out infinite', opacity: 0.6 }} />
  );
}

// ── Mascot ─────────────────────────────────────────────────────────────────────

function Mascot({ status, accent }: { status: string; accent: string }) {
  const bounce = status === 'called';
  const sleepy = status === 'waiting';
  const alert_ = status === 'urgent';
  const sad = ['skipped', 'cancelled'].includes(status);
  const happy = status === 'done';
  const holding = status === 'hold';

  return (
    <div style={{
      position: 'relative', width: 110, height: 130, margin: '0 auto 6px',
      animation: bounce ? 'mascot-jump 0.5s ease-in-out infinite' : sleepy ? 'mascot-breathe 3s ease-in-out infinite' : sad ? 'mascot-slouch 2.4s ease-in-out infinite' : 'none',
    }}>
      <svg viewBox="0 0 120 140" width="110" height="130">
        {/* Shadow */}
        <ellipse cx="60" cy="130" rx="26" ry="6" fill="#0f172a" opacity="0.08" />

        {/* Legs */}
        {bounce ? (
          <>
            <rect x="34" y="96" width="12" height="26" rx="6" fill={accent} transform="rotate(-18 40 100)" />
            <rect x="74" y="96" width="12" height="26" rx="6" fill={accent} transform="rotate(18 80 100)" />
            <ellipse cx="30" cy="118" rx="9" ry="6" fill="#0f172a" opacity="0.85" />
            <ellipse cx="90" cy="118" rx="9" ry="6" fill="#0f172a" opacity="0.85" />
          </>
        ) : (
          <>
            <rect x="42" y="98" width="13" height="26" rx="6.5" fill={accent} />
            <rect x="65" y="98" width="13" height="26" rx="6.5" fill={accent} />
            <ellipse cx="48.5" cy="124" rx="10" ry="6" fill="#0f172a" opacity="0.85" />
            <ellipse cx="71.5" cy="124" rx="10" ry="6" fill="#0f172a" opacity="0.85" />
          </>
        )}

        {/* Body (rounded torso) */}
        <ellipse cx="60" cy="80" rx="30" ry="26" fill={accent} />
        {/* Belly patch */}
        <ellipse cx="60" cy="86" rx="16" ry="13" fill="#fff" opacity="0.25" />

        {/* Little ticket the character holds (thematic to queue tokens) */}
        {!bounce && !sad && (
          <g transform="translate(84 74) rotate(18)">
            <rect x="0" y="0" width="18" height="12" rx="2" fill="#fff" stroke="#0f172a" strokeWidth="1.5" opacity="0.9" />
            <circle cx="4" cy="6" r="1.6" fill="#fff" stroke="#0f172a" strokeWidth="1" />
            <line x1="9" y1="3" x2="15" y2="3" stroke="#0f172a" strokeWidth="1.2" opacity="0.6" />
            <line x1="9" y1="6" x2="15" y2="6" stroke="#0f172a" strokeWidth="1.2" opacity="0.6" />
            <line x1="9" y1="9" x2="13" y2="9" stroke="#0f172a" strokeWidth="1.2" opacity="0.6" />
          </g>
        )}

        {/* Arms */}
        {holding ? (
          <>
            <rect x="26" y="72" width="20" height="9" rx="4.5" fill={accent} />
            <rect x="74" y="72" width="20" height="9" rx="4.5" fill={accent} />
          </>
        ) : bounce ? (
          <>
            <rect x="14" y="42" width="10" height="30" rx="5" fill={accent} transform="rotate(-30 19 57)" />
            <rect x="96" y="42" width="10" height="30" rx="5" fill={accent} transform="rotate(30 101 57)" />
          </>
        ) : sad ? (
          <>
            <rect x="22" y="82" width="22" height="9" rx="4.5" fill={accent} transform="rotate(20 33 86)" />
            <rect x="76" y="82" width="22" height="9" rx="4.5" fill={accent} transform="rotate(-20 87 86)" />
          </>
        ) : (
          <>
            <rect x="22" y="68" width="20" height="9" rx="4.5" fill={accent} />
          </>
        )}

        {/* Head */}
        <circle cx="60" cy="42" r="28" fill={accent} />
        {/* Cheeks */}
        {(bounce || happy) && (
          <>
            <circle cx="38" cy="48" r="4.5" fill="#fff" opacity="0.45" />
            <circle cx="82" cy="48" r="4.5" fill="#fff" opacity="0.45" />
          </>
        )}

        {/* Tuft of hair on top */}
        <path d={bounce ? "M56 16 q4 -14 10 -2" : "M58 16 q3 -10 7 -1"} stroke={accent} strokeWidth="5" fill="none" strokeLinecap="round" />

        {/* Face */}
        {sleepy ? (
          <>
            <path d="M46 40 q6 5 12 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M62 40 q6 5 12 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M52 54 q8 5 16 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <text x="88" y="16" fontSize="14" fontWeight="900" fill="#0f172a" opacity="0.55" style={{ animation: 'zzz-float 2.8s ease-in-out infinite' }}>Zzz</text>
          </>
        ) : alert_ ? (
          <>
            <circle cx="51" cy="38" r="5" fill="#0f172a" />
            <circle cx="69" cy="38" r="5" fill="#0f172a" />
            <ellipse cx="60" cy="54" rx="6" ry="8" fill="#0f172a" />
          </>
        ) : sad ? (
          <>
            <path d="M46 40 q5 -5 10 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M64 40 q5 -5 10 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M50 58 q10 -6 20 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        ) : bounce || happy ? (
          <>
            <path d="M46 36 q5 -8 10 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M64 36 q5 -8 10 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M48 50 q12 12 24 0" stroke="#0f172a" strokeWidth="3.5" fill="none" strokeLinecap="round" />
          </>
        ) : (
          <>
            <circle cx="51" cy="38" r="4" fill="#0f172a" />
            <circle cx="69" cy="38" r="4" fill="#0f172a" />
            <path d="M52 53 q8 6 16 0" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </>
        )}
      </svg>

      {/* Sparkles when called */}
      {bounce && (
        <>
          <span style={{ position: 'absolute', top: 0, left: 2, fontSize: 16, animation: 'sparkle-pop 1s ease-in-out infinite' }}>✨</span>
          <span style={{ position: 'absolute', top: 6, right: 0, fontSize: 14, animation: 'sparkle-pop 1s ease-in-out infinite 0.3s' }}>✨</span>
        </>
      )}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function QueueProgress({ ahead, total, accent }: { ahead: number; total: number; accent: string }) {
  const pct = total > 0 ? Math.max(0, Math.min(100, ((total - ahead) / total) * 100)) : 100;
  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
        <span>Start</span>
        <span>Your turn</span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: accent, borderRadius: 99, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function QueueTrackPage() {
  const params = new URLSearchParams(window.location.search);
  const tokenCode = params.get('token') || '';

  const [data, setData] = useState<TrackData | null>(null);
  const [error, setError] = useState('');
  const [lastStatus, setLastStatus] = useState('');
  const [notified, setNotified] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [audioReady, setAudioReady] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(true);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const intervalRef = useRef<any>(null);
  const tickRef = useRef<any>(null);
  const wasCalledRef = useRef(false);
  const alarmInterval = useRef<any>(null);
  const alertsEnabledRef = useRef(true);

  useEffect(() => { alertsEnabledRef.current = alertsEnabled; }, [alertsEnabled]);

  const fetchStatus = useCallback(async (silent = true) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await fetch(`${BASE}/queue/track/${tokenCode}`);
      if (!r.ok) { setError(r.status === 404 ? 'Token not found. Check your slip.' : `Error ${r.status}`); return; }
      const d: TrackData = await r.json();

      setData(prev => {
        // Notify when turn arrives
        if (d.status === 'called') {
          if (!alarmInterval.current) {
            if (alertsEnabledRef.current) { startAlarm(); vibrate(); }
            setNotified(true);
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`🔔 Your turn! Token ${String(d.token_number).padStart(3, '0')}`, {
                body: `Please proceed${d.counter_name ? ` to ${d.counter_name}` : ''}.`,
                icon: '/favicon.ico',
              });
            }
            alarmInterval.current = setInterval(() => {
              if (alertsEnabledRef.current) vibrate();
            }, 3000);
          }
        } else {
          if (alarmInterval.current) {
            clearInterval(alarmInterval.current);
            alarmInterval.current = null;
          }
          stopAlarm();
        }
        return d;
      });
      setLastStatus(d.status);
      setSecondsAgo(0);
      setError('');
    } catch {
      setError('Cannot reach server. Check your connection.');
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [tokenCode]);

  useEffect(() => {
    if (!tokenCode) { setError('No token code in URL'); return; }
    fetchStatus(false);
    intervalRef.current = setInterval(() => fetchStatus(true), 5000);
    tickRef.current = setInterval(() => setSecondsAgo(s => s + 1), 1000);

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
      if (alarmInterval.current) clearInterval(alarmInterval.current);
      stopAlarm();
    };
  }, [fetchStatus]);

  if (!tokenCode) return <ErrorScreen msg="No token code in URL" />;
  if (error && !data) return <ErrorScreen msg={error} onRetry={() => fetchStatus(false)} />;

  if (!data) return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 16,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: '#64748b', fontSize: 14 }}>Loading your token...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const cfg = STATUS_CONFIG[data.status] ?? STATUS_CONFIG.waiting;
  const isYourTurn = data.status === 'called';
  const isDone = ['done', 'skipped', 'cancelled'].includes(data.status);

  return (
    <div style={{
      minHeight: '100vh', background: cfg.bg,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '0 16px 40px',
    }}>

      {/* ── Header ── */}
      <div style={{ width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
     
          {data.counter_name && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{data.counter_label}: {data.counter_name}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: data.is_open ? '#22c55e' : '#ef4444' }} />
          <span style={{ fontSize: 11, color: data.is_open ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
            {data.is_open ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
      </div>

      {/* ── Permission modal (shown once on load) ── */}
      {showPermissionModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '28px 24px',
            maxWidth: 340, width: '100%', textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
              Enable turn alerts?
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
              We'll ring a bell and vibrate your phone the moment it's your turn, so you don't miss it.
            </div>
            <button
              onClick={() => {
                const ok = unlockAudio();
                setAudioReady(ok);
                setAlertsEnabled(true);
                setShowPermissionModal(false);
              }}
              style={{
                width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                background: '#6366f1', color: '#fff', fontWeight: 800, fontSize: 14,
                cursor: 'pointer', marginBottom: 10,
              }}
            >
              🔔 Enable Alerts
            </button>
            <button
              onClick={() => {
                setAlertsEnabled(false);
                setShowPermissionModal(false);
              }}
              style={{
                width: '100%', padding: '13px', borderRadius: 12,
                border: '1px solid #e2e8f0', background: 'transparent',
                color: '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* ── Small alerts toggle ── */}
      {!showPermissionModal && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, margin: '12px 20px 0',
        }}>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            {alertsEnabled ? '🔔 Alerts on' : '🔕 Alerts off'}
          </span>
          <button
            onClick={() => {
              const next = !alertsEnabled;
              setAlertsEnabled(next);
              if (next) { const ok = unlockAudio(); setAudioReady(ok); }
              else { stopAlarm(); if (alarmInterval.current) { clearInterval(alarmInterval.current); alarmInterval.current = null; } }
            }}
            style={{
              width: 40, height: 22, borderRadius: 99, border: 'none', cursor: 'pointer',
              background: alertsEnabled ? '#16a34a' : '#cbd5e1',
              position: 'relative', transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 2, left: alertsEnabled ? 20 : 2,
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </button>
        </div>
      )}

      {/* ── Your turn banner ── */}
      {isYourTurn && (
        <div style={{
          width: '100%', padding: '14px 20px', textAlign: 'center',
          background: 'linear-gradient(90deg, #15803d, #16a34a, #15803d)',
          animation: 'shimmer 2s ease-in-out infinite',
        }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: 1 }}>
            🔔 IT'S YOUR TURN — PLEASE PROCEED NOW
          </div>
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 420, padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 16, marginTop: 28, border: '2px solid #6366f1', borderRadius: 20, background: '#fff', boxShadow: '0 4px 24px rgba(99,102,241,0.1)' }}>

        {/* ── Token number hero ── */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <Mascot status={data.status} accent={cfg.accent} />
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>
            Your Token
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div style={{
              width: 140, height: 140, borderRadius: '50%', margin: '0 auto',
              background: cfg.card, border: `3px solid ${cfg.accent}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', position: 'relative',
            }}>
              {isYourTurn && <PulseRing color={cfg.accent} />}
              <div style={{ fontSize: 52, fontWeight: 900, color: '#0f172a', letterSpacing: 3, lineHeight: 1 }}>
                {String(data.token_number).padStart(3, '0')}
              </div>
              <div style={{ fontSize: 20, marginTop: 4 }}>{cfg.emoji}</div>
            </div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>{cfg.title}</div>
          <div style={{ fontSize: 13, color: cfg.text, marginTop: 4 }}>{cfg.subtitle}</div>
        </div>

        {/* ── Queue position card ── */}
        {!isDone && data.status !== 'hold' && (
          <div style={{ background: cfg.card, borderRadius: 16, padding: '20px 24px', border: `1px solid ${cfg.accent}22` }}>
            {isYourTurn ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: cfg.text, marginBottom: 8 }}>Now serving at counter</div>
                {data.counter_name && (
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#0f172a' }}>{data.counter_name}</div>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 36, fontWeight: 900, color: cfg.accent }}>{data.tokens_ahead}</div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Ahead of you</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 36, fontWeight: 900, color: cfg.accent }}>
                      {data.estimated_wait_mins < 1 ? '<1' : data.estimated_wait_mins}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Est. mins</div>
                  </div>
                </div>
                <QueueProgress ahead={data.tokens_ahead} total={data.tokens_ahead + data.token_number} accent={cfg.accent} />
                <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12, color: '#64748b' }}>
                  Currently serving: <strong style={{ color: '#0f172a' }}>
                    {data.current_number > 0 ? String(data.current_number).padStart(3, '0') : '---'}
                  </strong>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Token details card ── */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px 20px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: data.customer_label, value: data.customer_name },
              data.service ? { label: 'Service', value: data.service } : null,
              data.counter_name ? { label: data.counter_label, value: data.counter_name } : null,
              data.appointment_time ? { label: 'Time', value: data.appointment_time } : null,
              { label: 'Type', value: data.is_walk_in ? '🚶 Walk-in' : '📅 Appointment' },
              { label: 'Date', value: new Date(data.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) },
            ].filter(Boolean).map((row: any) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#64748b', fontWeight: 600 }}>{row.label}</span>
                <span style={{ color: '#1e293b', fontWeight: 700 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Refresh & last updated ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: '#475569' }}>
            {secondsAgo < 5 ? '✓ Just updated' : `Updated ${secondsAgo}s ago`} · Auto-refreshes every 5s
          </div>
          <button onClick={() => fetchStatus(false)} disabled={refreshing}
            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #334155', background: 'transparent', color: refreshing ? '#475569' : '#94a3b8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            {refreshing ? '...' : '↺ Refresh'}
          </button>
        </div>

        {/* ── Notification prompt ── */}
        {'Notification' in window && Notification.permission === 'default' && (
          <button onClick={() => Notification.requestPermission()}
            style={{ width: '100%', padding: '12px', borderRadius: 12, border: `1px solid ${cfg.accent}44`, background: cfg.card, color: cfg.text, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            🔔 Enable notifications for your turn
          </button>
        )}

        {/* ── Done state ── */}
        {isDone && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>{cfg.emoji}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{cfg.title}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{cfg.subtitle}</div>
            {data.status === 'skipped' && (
              <div style={{ marginTop: 16, fontSize: 13, color: '#f97316', background: 'rgba(249,115,22,0.1)', padding: '10px 16px', borderRadius: 10 }}>
                Please go to the reception desk and ask to be restored.
              </div>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <div style={{ textAlign: 'center', fontSize: 10, color: '#334155', marginTop: 8 }}>
          Token Code: {data.token_code}
        </div>
        <div style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: 2, color: '#1e293b', marginTop: 4 }}>
          POWERED BY CLOUDAIRAMBO™
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        @keyframes mascot-jump {
          0%, 100% { transform: translateY(0) rotate(-3deg); }
          50% { transform: translateY(-14px) rotate(3deg); }
        }
        @keyframes mascot-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
        @keyframes mascot-slouch {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(3px) rotate(-2deg); }
        }
        @keyframes zzz-float {
          0% { opacity: 0; transform: translateY(0); }
          30% { opacity: 0.6; }
          100% { opacity: 0; transform: translateY(-14px); }
        }
        @keyframes sparkle-pop {
          0%, 100% { transform: scale(0.8) rotate(0deg); opacity: 0.7; }
          50% { transform: scale(1.15) rotate(15deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── Error screen ──────────────────────────────────────────────────────────────

function ErrorScreen({ msg, onRetry }: { msg: string; onRetry?: () => void }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      padding: 24,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 48 }}>⚠️</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#ef4444' }}>{msg}</div>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#6366f1', color: '#0f172a', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Retry
        </button>
      )}
    </div>
  );
}
